import { IField } from 'app/services/api5-service/api.interface';
import { Observable, combineLatest, of } from 'rxjs';
import { filter, map, shareReplay } from 'rxjs/operators';

import { getOriginalControlFromConfig } from 'common/dynamic-form/utils/formly-configs-generation';
import {
  isFullWidth,
  isFullWidth$,
} from 'common/dynamic-form/utils/is-full-width';

import { getFieldButtons$ } from './buttons/buttons.utils';
import { getFieldDesc$ } from './description/description.utils';
import { getFieldHint$ } from './hint/hint.utils';
import { getFieldLabel$ } from './label/label.utils';
import { FieldLabel } from './label/model';
import { FieldSettings } from './model';
import { FieldBaseWrapperProps } from './model/field-base-props.interface';
import { getFieldPlainHintMsg$ } from './plain-hint/plain-hint.utils';
import { getFieldTag$ } from './tag/tag.utils';

import {
  ISPFieldConfig,
  ISPFieldTypeWithControl,
  ISPFormState,
} from '../../model';

/**
 * Get field settings. Some controls may want to override some behavior of field component, so we need to get this information from them
 *
 * @param config
 */
function getFieldSettings$(
  config: ISPFieldConfig,
): Observable<FieldSettings | undefined> {
  return config.props.fieldSettings$ || of(undefined);
}

/**
 * Get hint place stream
 *
 * @param label$
 * @param fieldSettings$
 */
function getHintPlace$(
  label$: Observable<FieldLabel | undefined>,
  fieldSettings$: Observable<FieldSettings | undefined>,
): Observable<FieldSettings['hintPlace']> {
  return combineLatest([label$, fieldSettings$]).pipe(
    map(([label, fieldSettings]) => {
      if (!label) {
        return 'field';
      }

      return fieldSettings?.hintPlace || 'label';
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}

/**
 * Get tag place stream
 *
 * @param label$
 * @param fieldSettings$
 */
function getTagPlace$(
  label$: Observable<FieldLabel | undefined>,
  fieldSettings$: Observable<FieldSettings | undefined>,
): Observable<FieldSettings['tagPlace']> {
  return combineLatest([label$, fieldSettings$]).pipe(
    map(([label, fieldSettings]) => {
      if (!label) {
        return 'field';
      }

      return fieldSettings?.tagPlace || 'label';
    }),
    shareReplay({
      refCount: true,
      bufferSize: 1,
    }),
  );
}

/**
 * Get props for field base field wrapper
 *
 * @param field
 * @param config
 * @param state
 * @returns
 */
function getFieldBaseProps(
  field: IField,
  config: ISPFieldConfig,
  state: ISPFormState,
): FieldBaseWrapperProps {
  const fieldSettings$ = getFieldSettings$(config);
  const desc$ = getFieldDesc$(field, state, fieldSettings$);
  const label$ = getFieldLabel$(field, state, fieldSettings$, desc$);

  return {
    isFullWidth: isFullWidth(field, getOriginalControlFromConfig(config)),
    label$,
    hint$: getFieldHint$(field, state),
    hintPlace$: getHintPlace$(label$, fieldSettings$),
    tagData$: getFieldTag$(field, state),
    tagPlace$: getTagPlace$(label$, fieldSettings$),
    fieldDisplayMode$: fieldSettings$.pipe(
      map(fieldSettings => fieldSettings?.displayMode || 'center'),
    ),
    fieldDesc$: desc$.pipe(filter(desc => desc?.position === 'under')),
    fieldPlainHintMsg$: getFieldPlainHintMsg$(field, state),
    fieldButtons$: getFieldButtons$(field, state, fieldSettings$),
  };
}

/**
 * Append props, specific for field-base field wrapper
 *
 * @param config - config to append
 * @param field - field metadata
 * @param state - dynamic form state
 */
export function appendFieldBaseOptions(
  config: ISPFieldConfig,
  field: IField,
  state: ISPFormState,
): void {
  const props = getFieldBaseProps(field, config, state);

  config.props = {
    ...props,
    ...config.props,
  };

  config.expressions = {
    'props.isFullWidth': isFullWidth$(
      field,
      config as ISPFieldConfig<ISPFieldTypeWithControl>,
    ),
    ...config.expressions,
  };
}
